import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../utils/authContext';
import { useToast } from '../../components/Auth/Toast';
import { ToastContainer } from '../../components/Auth/Toast';
import Spinner from '../../components/Auth/Spinner';
import './AuthPages.css';

const ResetPasswordPage = () => {
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [completed, setCompleted] = useState(false);
  const { token } = useParams();
  const { resetPassword, loading, error, setError } = useAuth();
  const { toasts, addToast, removeToast } = useToast();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validateForm = () => {
    if (!formData.password) {
      addToast('Please enter a new password', 'error');
      return false;
    }
    if (formData.password.length < 6) {
      addToast('Password must be at least 6 characters', 'error');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      addToast('Passwords do not match', 'error');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;

    if (!token) {
      addToast('Invalid reset link', 'error');
      return;
    }

    try {
      await resetPassword(token, formData);
      addToast('Password reset successfully!', 'success');
      setCompleted(true);
      setTimeout(() => {
        navigate('/login');
      }, 2500);
    } catch (err) {
      addToast(error || 'Failed to reset password', 'error');
    }
  };

  return (
    <div className='auth-container'>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      
      <div className='auth-page-wrapper'>
        {/* Logo Section */}
        <div className='auth-logo-section'>
          <div className='auth-logo-container'>
            <img
              src={require('../../assets/logo/logo1.jpeg')}
              alt='Laat Sahab News Logo'
              className='auth-logo-img'
            />
            <div className='auth-logo-text'>Laat Sahab News</div>
          </div>
        </div>

        {/* Form Section */}
        <div className='auth-form-wrapper'>
          <div className='auth-header'>
            <h1>🔑 Create New Password</h1>
            <p>Enter your new password below</p>
          </div>

          {completed ? (
            <div className='success-message'>
              <div className='success-icon'>✅</div>
              <h2>Password Updated!</h2>
              <p>Your password has been successfully reset.</p>
              <p className='small-text'>
                You can now log in with your new password.
              </p>
              <Link to='/login' className='auth-button'>
                Go to Login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className='auth-form'>
              <div className='form-group'>
                <label htmlFor='password'>New Password</label>
                <div className='password-input-wrapper'>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id='password'
                    name='password'
                    value={formData.password}
                    onChange={handleChange}
                    placeholder='Enter your new password'
                    disabled={loading}
                  />
                  <button
                    type='button'
                    className='password-toggle'
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
                <p className='input-hint'>
                  Use at least 6 characters with a mix of letters and numbers
                </p>
              </div>

              <div className='form-group'>
                <label htmlFor='confirmPassword'>Confirm Password</label>
                <div className='password-input-wrapper'>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id='confirmPassword'
                    name='confirmPassword'
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder='Confirm your password'
                    disabled={loading}
                  />
                  <button
                    type='button'
                    className='password-toggle'
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
              </div>

              <button
                type='submit'
                className='auth-button'
                disabled={loading}
              >
                {loading ? <Spinner size='sm' color='white' /> : 'Reset Password'}
              </button>
            </form>
          )}

          <div className='auth-footer'>
            <p>
              Remember your password?{' '}
              <Link to='/login' className='auth-link'>
                Login here
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
